import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Usuario, LoginData, AuthContextType } from '@/types';
import { AuthService, UsuariosService } from '@/services';
import { StorageService } from '@/utils/storage';
import toast from 'react-hot-toast';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedToken = StorageService.getToken();
    
    if (savedToken) {
      setToken(savedToken);
      fetchUserProfile();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      const userData = await UsuariosService.getPerfil();
      
      if (userData.id) {
        try {
          const relacionamentos = await UsuariosService.getUsuarioRelacionamentos(userData.id);
          
          const usuarioCompleto = {
            ...userData,
            departamentos: relacionamentos.departamentos,
            unidades: relacionamentos.unidades
          };
          
          setUsuario(usuarioCompleto);
        } catch (error) {
          console.warn('AuthContext: Erro ao buscar relacionamentos, usando dados básicos:', error);
          setUsuario(userData);
        }
      } else {
        setUsuario(userData);
      }
    } catch (error) {
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (data: LoginData): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await AuthService.login(data);
      
      if (!response.token || response.token.length < 10) {
        throw new Error('Email ou senha inválidos.');
      }
      
      StorageService.setToken(response.token, true);
      setToken(response.token);
      
      try {
        const perfilCompleto = await UsuariosService.getPerfil();
        const relacionamentos = await UsuariosService.getUsuarioRelacionamentos(response.usuario.id);
        
        const usuarioCompleto = {
          ...response.usuario,
          ...perfilCompleto,
          departamentos: relacionamentos.departamentos,
          unidades: relacionamentos.unidades
        };
        
        setUsuario(usuarioCompleto);
      } catch (error) {
        console.warn('AuthContext: Erro ao buscar relacionamentos, usando dados básicos:', error);
        setUsuario(response.usuario);
      }
      
      setIsLoading(false);
      
      toast.success('Login realizado com sucesso!');
    } catch (error: any) {
      setIsLoading(false);
      toast.error(error.message || 'Erro ao fazer login');
      throw error;
    }
  };

  const logout = (): void => {
    setUsuario(null);
    setToken(null);
    StorageService.clearAuth();
    toast.success('Logout realizado com sucesso!');
  };

  const updateUsuario = (updatedUser: Usuario): void => {
    setUsuario(updatedUser);
  };

  const value: AuthContextType = {
    usuario,
    token,
    login,
    logout,
    updateUsuario,
    isAuthenticated: !!token && !!usuario,
    isLoading,
  };



  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
